import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { History, Clock, CheckCircle2, XCircle, Send } from 'lucide-react';
import ModalShell, { ModalFooter } from '../common/ModalShell';
import { sendShareRequest, fetchSentShareRequests, latestRequestFor } from '../../redux/appointmentShareSlice';

// "ASK TO SEE PREVIOUS VISITS" (visit sharing v2, owner 24 Sep 2026).
//
// Dr Kumar is treating Kiran today; Kiran saw Dr Arvind at another hospital
// last month. Dr Kumar cannot read that visit — it is another hospital's
// record — but he can ASK, and Kiran decides in his own app which visits (if
// any) to share. So this is a button, then a status chip:
//   · no ask yet, or the last one expired → "Ask to see previous visits";
//   · pending   → "Asked · waiting";
//   · granted   → "Granted · N visits", linking to Shared with me;
//   · rejected  → "Declined".
// The chip is the server's list (`GET …/requests/sent`), matched by the
// person's id — not local memory — so it is right after a reload and on the
// other device, and the socket bridge flips it live when the patient answers.
//
// Only a LINKED doctor sees it: the server refuses anyone else (403
// NOT_A_LINKED_PATIENT), and a button that always fails is worse than none.

const NOTE_MAX = 300;

function AskPreviousVisits({ patientId, patientName, inVisit = false }) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { sentRequests, sentLoaded, sending } = useSelector((state) => state.appointmentShare);
    const [open, setOpen] = useState(false);
    const [note, setNote] = useState('');
    const noteRef = useRef(null);

    useEffect(() => {
        // One read per mount: the drawer and the tab each mount this once.
        dispatch(fetchSentShareRequests());
    }, [dispatch]);

    useEffect(() => {
        if (!open) return undefined;
        noteRef.current?.focus();
        // Esc closes THIS modal only; stopping it keeps the drawer underneath
        // (which also listens for Esc) open.
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopImmediatePropagation(); setOpen(false); }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [open]);

    const latest = latestRequestFor(sentRequests, patientId);
    const name = patientName || t('shareRequests.the_patient', 'the patient');

    const submit = async (e) => {
        e.preventDefault();
        const res = await dispatch(sendShareRequest({ patientId, note }));
        if (sendShareRequest.fulfilled.match(res)) {
            toast.success(t('shareRequests.sent_toast', { name, defaultValue: `Asked ${name} to share previous visits` }));
            setOpen(false);
            setNote('');
            return;
        }
        const err = res.payload || {};
        if (err.status === 409 || err.code === 'REQUEST_PENDING') {
            // Somebody (this doctor on another tab, or a double click) already
            // asked. Not a failure: re-read so the chip shows the open ask.
            toast.info(t('shareRequests.already_asked', 'Already asked — waiting for the patient'));
            setOpen(false);
            dispatch(fetchSentShareRequests());
        } else if (err.code === 'NOT_A_LINKED_PATIENT') {
            toast.error(t('shareRequests.not_linked', 'You can ask only a patient you have an appointment with.'));
        } else {
            toast.error(err.message || t('shareRequests.send_failed', 'Could not send the request. Please try again.'));
        }
    };

    // Until the list arrives, draw nothing rather than a button that may be
    // replaced a moment later by "Asked · waiting".
    if (!sentLoaded) return null;

    const chipBase = 'inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-semibold';

    if (latest?.status === 'pending') {
        return (
            <span data-testid="ask-visits-chip" data-status="pending" className={`${chipBase} border-[#D7A05C]/40 bg-[#D7A05C]/10 text-[#9A6B2E]`}>
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {t('shareRequests.chip_pending', 'Asked · waiting')}
            </span>
        );
    }
    if (latest?.status === 'granted') {
        const label = t('shareRequests.chip_granted', { count: latest.grantedCount || 0, defaultValue: `Granted · ${latest.grantedCount || 0} visits` });
        const cls = `${chipBase} border-[#4ABDBD]/40 bg-[#4ABDBD]/10 text-[#2D8A8A] hover:bg-[#4ABDBD]/20`;
        const inner = (<><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />{label}</>);
        // Inside an open visit, leaving the page is how half-typed notes get
        // lost — so there the link opens Shared with me in a new tab.
        return inVisit ? (
            <a data-testid="ask-visits-chip" data-status="granted" href="#/shared-visits" target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>
        ) : (
            <Link data-testid="ask-visits-chip" data-status="granted" to="/shared-visits" className={cls}>{inner}</Link>
        );
    }
    if (latest?.status === 'rejected') {
        return (
            <span data-testid="ask-visits-chip" data-status="rejected" className={`${chipBase} border-gray-200 bg-gray-50 text-gray-500`}>
                <XCircle className="h-3.5 w-3.5" aria-hidden="true" />
                {t('shareRequests.chip_rejected', 'Declined')}
            </span>
        );
    }

    return (
        <>
            <button
                type="button"
                data-testid="ask-visits-button"
                onClick={() => setOpen(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#4ABDBD]/40 bg-white px-2.5 py-1 text-xs font-semibold text-[#2D8A8A] transition-colors hover:bg-[#4ABDBD]/10"
            >
                <History className="h-3.5 w-3.5" aria-hidden="true" />
                {t('shareRequests.ask_button', 'Ask to see previous visits')}
            </button>
            {open && typeof document !== 'undefined' && createPortal(
                // Above the health record drawer (z-60), portalled like it.
                // Click on the dimmed page closes, as before the shared frame.
                <div onClick={(e) => { if (e.target === e.currentTarget.firstElementChild) setOpen(false); }}>
                <ModalShell
                    title={t('shareRequests.modal_title', 'Ask to see previous visits')}
                    onClose={() => setOpen(false)}
                    closeLabel={t('shareRequests.cancel', 'Cancel')}
                    size="md"
                    zClass="z-[70]"
                    testId="ask-visits-modal-root"
                    closeTestId="ask-visits-cancel-x"
                    footer={(
                        <ModalFooter
                            onCancel={() => setOpen(false)}
                            cancelLabel={t('shareRequests.cancel', 'Cancel')}
                            submitType="submit"
                            form="ask-visits-form"
                            submitLabel={<><Send className="h-4 w-4" aria-hidden="true" />{t('shareRequests.send', 'Send request')}</>}
                            disabled={sending}
                            cancelTestId="ask-visits-cancel"
                            submitTestId="ask-visits-submit"
                        />
                    )}
                >
                    <form
                        id="ask-visits-form"
                        data-testid="ask-visits-modal"
                        onSubmit={submit}
                    >
                        <p className="mb-3 text-sm text-gray-600">
                            {t('shareRequests.modal_body', { name, defaultValue: `${name} will be asked in their app, and chooses which visits to share with you.` })}
                        </p>
                        <label htmlFor="ask-visits-note" className="mb-1 block text-xs font-semibold text-gray-500">
                            {t('shareRequests.note_label', 'Note to the patient (optional)')}
                        </label>
                        <textarea
                            id="ask-visits-note"
                            ref={noteRef}
                            data-testid="ask-visits-note"
                            value={note}
                            maxLength={NOTE_MAX}
                            rows={3}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={t('shareRequests.note_placeholder', 'e.g. To see how your headache was treated')}
                            className="w-full rounded-xl border border-gray-200 p-2 text-sm focus:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
                        />
                        <p className="mt-0.5 text-right text-[10px] text-gray-400">{note.length}/{NOTE_MAX}</p>
                    </form>
                </ModalShell>
                </div>,
                document.body
            )}
        </>
    );
}

export default AskPreviousVisits;
